import { API_ENDPOINTS, apiService } from "../api";
import type {
  ConnectorCreate,
  ConnectorDailyRunsRead,
  ConnectorCredentialsUpdate,
  ConnectorDatasetQuery,
  ConnectorDatasetRead,
  ConnectorDetailRead,
  ConnectorIngestionStateRead,
  ConnectorListQuery,
  ConnectorRead,
  ConnectorRunQuery,
  ConnectorRunRead,
  ConnectorSourceRequestCreate,
  ConnectorSourceRequestRead,
  ConnectorTypeListResponse,
  ConnectorTypeRead,
  ConnectorUsageListResponse,
  ConnectorUpdate,
  ConnectorWriteRead,
  DriftEventRead,
  DriftResolution,
  OffsetPage,
  OffsetPageQuery,
  SelectionAppliedRead,
  SelectionCreate,
  SyncRunRead,
  UploadCreate,
  UploadCreatedRead,
  UploadModelRead,
  UploadRead,
} from "./connectorsV2.types";

const paths = API_ENDPOINTS.connectorsV2;

/** The types a tenant can add, each with its form. */
export const fetchConnectorTypes = async (): Promise<ConnectorTypeRead[]> =>
  (await apiService.get<ConnectorTypeListResponse>(paths.types)).data;

/** Every active catalog row: coming-soon ones too (`addable` false), with counts. */
export const fetchConnectorCatalog = async (): Promise<ConnectorTypeRead[]> =>
  (await apiService.get<ConnectorTypeListResponse>(paths.types, { include: "catalog" })).data;

/** 204; idempotent. */
export const requestConnectorNotify = (key: string): Promise<void> =>
  apiService.post<void>(paths.typeNotify(key), {});

/** 204; idempotent. */
export const cancelConnectorNotify = (key: string): Promise<void> =>
  apiService.delete<void>(paths.typeNotify(key));

/** 201 */
export const requestConnectorSource = (
  body: ConnectorSourceRequestCreate,
): Promise<ConnectorSourceRequestRead> =>
  apiService.post<ConnectorSourceRequestRead>(paths.sourceRequests, body);

/** The run heatmap: every connector's runs per day. */
export const fetchConnectorDailyRuns = (days = 7): Promise<ConnectorDailyRunsRead> =>
  apiService.get<ConnectorDailyRunsRead>(paths.dailyRuns, { days });

export const fetchConnectorRuns = (id: string, query: ConnectorRunQuery = {}): Promise<OffsetPage<ConnectorRunRead>> =>
  apiService.get<OffsetPage<ConnectorRunRead>>(paths.runs(id), query);

/** Stories and data apps that read this connector's data. */
export const fetchConnectorUsedIn = (id: string): Promise<ConnectorUsageListResponse> =>
  apiService.get<ConnectorUsageListResponse>(paths.usedIn(id));

export const fetchConnectors = (query: ConnectorListQuery = {}): Promise<OffsetPage<ConnectorRead>> =>
  apiService.get<OffsetPage<ConnectorRead>>(paths.list, query);

export const fetchConnector = (id: string): Promise<ConnectorDetailRead> =>
  apiService.get<ConnectorDetailRead>(paths.item(id));

/** 201; onboarding starts after the commit (`onboarding_requested` false: it didn't). */
export const createConnector = (body: ConnectorCreate): Promise<ConnectorWriteRead> =>
  apiService.post<ConnectorWriteRead>(paths.list, body);

/** Name and environment only. */
export const updateConnector = (id: string, body: ConnectorUpdate): Promise<ConnectorRead> =>
  apiService.patch<ConnectorRead>(paths.item(id), body);

/** Removes the connector and, through Ingestion, its schedule and loaded data. */
export const deleteConnector = (id: string): Promise<void> =>
  apiService.delete<void>(paths.item(id));

/** 202; replaces the whole credential and runs onboarding again. */
export const updateConnectorCredentials = (
  id: string,
  body: ConnectorCredentialsUpdate,
): Promise<ConnectorWriteRead> => apiService.put<ConnectorWriteRead>(paths.credentials(id), body);

/** 202; runs onboarding again after `failed` or a start that didn't happen. */
export const retryConnectorOnboarding = (id: string): Promise<void> =>
  apiService.post<void>(paths.onboarding(id), {});

/** 202; starts onboarding for a connector made elsewhere (a legacy sign-in popup's `connection_id`). */
export const onboardConnector = (id: string): Promise<void> =>
  apiService.post<void>(paths.onboarding(id), {});

export const fetchConnectorIngestionState = (id: string): Promise<ConnectorIngestionStateRead> =>
  apiService.get<ConnectorIngestionStateRead>(paths.ingestionState(id));

export const fetchConnectorDatasets = (
  id: string,
  query: ConnectorDatasetQuery = {},
): Promise<OffsetPage<ConnectorDatasetRead>> =>
  apiService.get<OffsetPage<ConnectorDatasetRead>>(paths.datasets(id), query);

/** 202; the refresh it asks for loads (Ingest) or registers (Live) what was selected. */
export const applyConnectorSelection = (
  id: string,
  body: SelectionCreate,
): Promise<SelectionAppliedRead> => apiService.post<SelectionAppliedRead>(paths.selection(id), body);

/** 202; one asked for during a run joins its next pass. */
export const refreshConnector = (id: string): Promise<void> =>
  apiService.post<void>(paths.refresh(id), {});

export const fetchConnectorSyncs = (
  id: string,
  query: OffsetPageQuery = {},
): Promise<OffsetPage<SyncRunRead>> => apiService.get<OffsetPage<SyncRunRead>>(paths.syncs(id), query);

export const fetchConnectorUploads = (
  id: string,
  query: OffsetPageQuery = {},
): Promise<OffsetPage<UploadRead>> => apiService.get<OffsetPage<UploadRead>>(paths.uploads(id), query);

export const fetchConnectorUpload = (id: string, uploadId: string): Promise<UploadRead> =>
  apiService.get<UploadRead>(paths.upload(id, uploadId));

/** Whether the data model built from this upload is ready; derived on the server per upload. */
export const fetchUploadModel = (id: string, uploadId: string): Promise<UploadModelRead> =>
  apiService.get<UploadModelRead>(paths.uploadModel(id, uploadId));

/** 201: a signed URL to `PUT` the file to. */
export const requestConnectorUpload = (id: string, body: UploadCreate): Promise<UploadCreatedRead> =>
  apiService.post<UploadCreatedRead>(paths.uploads(id), body);

/** 202: the file is in the bucket; Ingestion loads it. */
export const completeConnectorUpload = (id: string, uploadId: string): Promise<UploadRead> =>
  apiService.post<UploadRead>(paths.uploadComplete(id, uploadId), {});

/**
 * Sends the file straight to the bucket. Not through `apiService`: the signed URL is absolute and
 * must get exactly the given headers, never the app's token.
 */
export const putFileToSignedUrl = async (
  url: string,
  headers: Record<string, string>,
  file: Blob,
): Promise<void> => {
  const response = await fetch(url, { method: "PUT", headers, body: file });
  if (!response.ok) {
    throw Object.assign(new Error(`The file didn't reach storage (${response.status}).`), {
      status: response.status,
    });
  }
};

export const fetchConnectorDriftEvents = (
  id: string,
  unresolved?: boolean,
): Promise<DriftEventRead[]> =>
  apiService.get<DriftEventRead[]>(
    paths.driftEvents(id),
    unresolved === undefined ? undefined : { unresolved },
  );

/** `accepted` loads a held file with its new columns; `rejected` keeps the current table. */
export const resolveConnectorDriftEvent = (
  id: string,
  eventId: string,
  resolution: DriftResolution,
): Promise<DriftEventRead> =>
  apiService.post<DriftEventRead>(paths.driftResolution(id, eventId), { resolution });

export type UploadStage = "requesting" | "uploading" | "completing";

/** One file: ask for a signed URL, `PUT` the bytes there, then tell api-service it arrived. */
export const uploadFileToConnector = async (
  id: string,
  file: File,
  onStage?: (stage: UploadStage) => void,
  /** Overrides `file.type` (some systems report a CSV as another type); the signed `PUT` must send the same value. */
  contentType?: string,
): Promise<UploadRead> => {
  onStage?.("requesting");
  const created = await requestConnectorUpload(id, {
    file_name: file.name,
    size_bytes: file.size,
    content_type: contentType ?? (file.type || "text/csv"),
  });
  onStage?.("uploading");
  await putFileToSignedUrl(created.url, created.headers, file);
  onStage?.("completing");
  return completeConnectorUpload(id, created.upload.id);
};
